import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import googleDriveService from '../services/googleDriveService';
import Footer from "../views/Footer";
import FadeIn from "../components/FadeIn";
import { Container, Typography, ImageCard } from "../components/ui";
import SkeletonLoader from "../components/SkeletonLoader";

const CategoryGallery = () => {
  const { categoryName } = useParams();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const getGoogleDriveDirectUrl = (url) => {
    if (!url) return null;
    const match =
      url.match(/\/d\/([a-zA-Z0-9_-]+)/) ||
      url.match(/id=([a-zA-Z0-9_-]+)/);
    if (!match) return url;
    const fileId = match[1];
    return `https://lh3.googleusercontent.com/d/${fileId}=w2000`;
  };

  useEffect(() => {
    const loadCategoryImages = async () => {
      try {
        // Initialize Google Drive Service
        const initialized = await googleDriveService.initialize();

        if (!initialized) {
          console.error('❌ Google Drive Service not initialized');
          setLoading(false);
          return;
        }

        // Auto-detect gallery structure
        const result = await googleDriveService.autoDetectGalleryStructure();

        // Filter images by category name
        const categoryImages = result.images.filter(
          (img) => img.category === categoryName
        );

        setImages(categoryImages);
      } catch (error) {
        console.error("Failed to load category images:", error);
        setImages([]);
      }
      setLoading(false);
    };

    loadCategoryImages();
  }, [categoryName]);

  const openModal = (index) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  return (
    <>
      <Container.Section padding="hero" background="gradient">
        <Container size="standard">
          <Typography.SectionHeader
            title={<span className="text-gradient">{categoryName}</span>}
            description="Explore the moments captured in this category."
            align="center"
          />
        </Container>
      </Container.Section>

      <Container.Section padding="content" background="white">
        <Container size="standard">
          {loading ? (
            <SkeletonLoader type="gallery" count={6} />
          ) : (
            <Container.Grid columns="3" gap="default">
              {images.map((item, index) => (
                <FadeIn key={index} delay={0.1 + index * 0.05}>
                  <div className="cursor-pointer" onClick={() => openModal(index)}>
                    <ImageCard item={item} />
                  </div>
                </FadeIn>
              ))}
            </Container.Grid>
          )}
        </Container>
      </Container.Section>

      {isModalOpen && images.length > 0 && (
        <div className="fixed inset-0 z-[9999] bg-black/80 flex justify-center items-center p-4">

          <div className="relative bg-white rounded-2xl shadow-2xl p-4 flex items-center justify-center"
            style={{ width: "900px", height: "650px" }}>

            {/* Close Button */}
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-black text-3xl font-bold transition"
              onClick={closeModal}
            >
              ✕
            </button>

            {/* Image Same Size */}
            <img
              src={getGoogleDriveDirectUrl(images[currentIndex].imageurl)}
              alt=""
              className="rounded-lg object-cover"
              style={{ width: "100%", height: "100%" }}
            />

            {/* Arrows inside white background close to image */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() =>
                    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
                  }
                  className="absolute left-4 top-1/2 -translate-y-1/2 
                            text-gray-700 hover:text-black text-4xl font-bold transition"
                >
                  ❮
                </button>

                <button
                  onClick={() =>
                    setCurrentIndex((prev) => (prev + 1) % images.length)
                  }
                  className="absolute right-4 top-1/2 -translate-y-1/2 
                            text-gray-700 hover:text-black text-4xl font-bold transition"
                >
                  ❯
                </button>
              </>
            )}

          </div>

        </div>
      )}

      <Footer />
    </>
  );
};

export default CategoryGallery;
